// ══════════════════════════════════════════════════════════════════
//  ED CRM - Service de Activities (núcleo/aplicação)
// ══════════════════════════════════════════════════════════════════
//  CRUD de atividades + classificacao por urgencia (overdue/today/upcoming).
//  Recebe o repositório via porta (DI) — não conhece Supabase.
//  Funções puras (classificação, metadados) exportadas direto do módulo.
// ══════════════════════════════════════════════════════════════════

import type { ActivityEntry, ActivityTypeMeta } from '@/types/activity';
import type { IActivitiesRepository, TeamMemberRow } from '../ports/IActivitiesRepository';

// ─── Funções puras (sem Supabase) ───────────────────────────────

// Retorna o responsável "do card" (não do assigned_to) — usado para filtrar
// atividades por responsável real, já que SDR/Closer da etapa nem sempre
// coincidem com o assigned_to gravado na criação da atividade.
export const cardOwnerForActivity = (
  activity: Pick<ActivityEntry, 'card'> | null | undefined,
): string | null => {
  const role = activity?.card?.pipelines?.role;
  if (role === 'closer') return activity?.card?.closer_id || null;
  return activity?.card?.sdr_id || null;
};

export const ACTIVITY_TYPES: ActivityTypeMeta[] = [
  { key: 'whatsapp',  label: 'WhatsApp',  icon: 'message-circle', color: '#16A34A' },
  { key: 'ligacao',   label: 'Ligacao',   icon: 'phone', color: '#2563C8' },
  { key: 'email',     label: 'E-mail',    icon: 'mail', color: '#7C3AED' },
  { key: 'follow_up', label: 'Follow-up', icon: 'repeat', color: '#D97706' },
  { key: 'reuniao',   label: 'Reuniao',   icon: 'handshake', color: '#0891B2' },
  { key: 'outro',     label: 'Outro',     icon: 'dot',  color: '#6B7280' },
];

export const getTypeMeta = (type: string): ActivityTypeMeta =>
  ACTIVITY_TYPES.find(t => t.key === type) || ACTIVITY_TYPES[ACTIVITY_TYPES.length - 1];

// ─── Classificacao por urgencia ─────────────────────────────────
export interface UrgencyBuckets<T> {
  overdue: T[];
  today: T[];
  upcoming: T[];
}

export const classifyByUrgency = <T extends { due_date?: string | null }>(
  activities: T[] = [],
  now: Date = new Date(),
): UrgencyBuckets<T> => {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const overdue: T[] = [];
  const today: T[] = [];
  const upcoming: T[] = [];

  for (const a of activities) {
    if (!a.due_date) { upcoming.push(a); continue; }
    const due = new Date(a.due_date);
    if (due < startOfToday)       overdue.push(a);
    else if (due <= endOfToday)   today.push(a);
    else                          upcoming.push(a);
  }

  return { overdue, today, upcoming };
};

// ─── Webhook n8n (fire-and-forget, sem Supabase) ────────────────
const fireCadenceWebhook = async (activity: ActivityEntry | null): Promise<void> => {
  const url = process.env.REACT_APP_N8N_WEBHOOK_CADENCIA;
  if (!url || !activity) return;
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        activity_id: activity.id,
        card_id:     activity.card_id,
        lead_id:     activity.lead_id,
        type:        activity.type,
        result:      'done',
      }),
    });
  } catch (err) {
    console.warn('[activities] webhook falhou (silencioso):', err);
  }
};

// ─── Parâmetros públicos do service ─────────────────────────────
export interface ListPendingActivitiesParams {
  workspaceId: string;
  assigneeId?: string | null;
  type?: string | null;
  funnelId?: string | null;
}

export interface CreateActivityParams {
  workspaceId: string;
  cardId: string;
  leadId: string;
  assignedTo?: string | null;
  type: string;
  title: string;
  description?: string;
  script?: string;
  dueDate: string;
  /**
   * Antecedências do lembrete, em minutos antes do vencimento (0 = no horário).
   * null/undefined ⇒ o trigger usa a preferência do usuário (Config/103).
   */
  remindBeforeMinutes?: number[] | null;
}

// ─── Service ────────────────────────────────────────────────────
export class ActivitiesService {
  constructor(private readonly repo: IActivitiesRepository) {}

  listActivitiesByLead = (leadId: string): Promise<ActivityEntry[]> => {
    if (!leadId) return Promise.resolve([]);
    return this.repo.listByLead(leadId);
  };

  listActivityTeam = (workspaceId: string): Promise<TeamMemberRow[]> => {
    if (!workspaceId) return Promise.resolve([]);
    return this.repo.listTeamMembers(workspaceId);
  };

  listPendingActivities = async ({
    workspaceId, assigneeId = null, type = null, funnelId = null,
  }: ListPendingActivitiesParams): Promise<ActivityEntry[]> => {
    if (!workspaceId) return [];
    let list = await this.repo.listPendingActivities({ workspaceId, type });
    if (assigneeId) {
      list = list.filter(a => cardOwnerForActivity(a) === assigneeId);
    }
    if (funnelId) {
      list = list.filter(a => a?.card?.pipelines?.funnel_id === funnelId);
    }
    return list;
  };

  createActivity = async ({
    workspaceId, cardId, leadId, assignedTo,
    type, title, description = '', script = '', dueDate,
    remindBeforeMinutes = null,
  }: CreateActivityParams): Promise<ActivityEntry> => {
    if (!workspaceId) throw new Error('workspaceId e obrigatorio.');
    if (!cardId)      throw new Error('cardId e obrigatorio.');
    if (!leadId)      throw new Error('leadId e obrigatorio.');
    if (!type)        throw new Error('type e obrigatorio.');
    if (!title)       throw new Error('title e obrigatorio.');
    if (!dueDate)     throw new Error('dueDate e obrigatorio.');

    return this.repo.insertActivity({
      workspaceId, cardId, leadId, assignedTo,
      type, title, description, script, dueDate,
      // Lista vazia = "sem lembrete"; distinguir de null (usar a preferência).
      remindBeforeMinutes: remindBeforeMinutes ?? null,
    });
  };

  completeActivity = async (
    id: string,
    { comment }: { comment?: string } = {},
  ): Promise<ActivityEntry> => {
    const data = await this.repo.markActivityDone(id);

    // Se o usuário deixou um comentário ao concluir, registra em history
    // (PR-6 item 15 — doc v11 seção 3.2).
    const trimmed = (comment || '').trim();
    if (trimmed && data?.lead_id) {
      try {
        await this.repo.insertHistory({
          workspace_id: data.workspace_id,
          lead_id:      data.lead_id,
          card_id:      data.card_id || null,
          type:         'comentario',
          title:        `Comentário em "${data.title || 'atividade'}"`,
          description:  trimmed,
          data: {
            activity_id:   data.id,
            activity_type: data.type,
          },
        });
      } catch (err) {
        // Comentário é opcional — falha não deve estourar o fluxo de conclusão.
        // eslint-disable-next-line no-console
        console.error('[activities] history comentario falhou:', err);
      }
    }

    fireCadenceWebhook(data).catch(err => console.warn('[activities] webhook cadencia:', err));
    return data;
  };

  // Cancela todas as atividades 'pending' de um card. Usado quando o card
  // muda de etapa — as atividades atreladas à etapa anterior deixam de
  // fazer sentido, e a trigger de cadência cria as da etapa nova.
  listPendingActivityIdsForCard = async (
    cardId: string | null | undefined,
  ): Promise<string[]> => {
    if (!cardId) return [];
    return this.repo.listPendingActivityIdsForCard(cardId);
  };

  cancelActivitiesByIds = async (ids: string[]): Promise<number> => {
    if (!ids || ids.length === 0) return 0;
    await this.repo.cancelActivitiesByIds(ids);
    return ids.length;
  };

  cancelActivity = async (id: string): Promise<ActivityEntry> => {
    return this.repo.cancelActivity(id);
  };

  // Desfaz concluir/cancelar. Sem webhook de cadência nem history: reabrir é
  // correção de engano, não um evento do funil.
  reopenActivity = async (id: string): Promise<ActivityEntry> => {
    return this.repo.reopenActivity(id);
  };

  // Adia a atividade somando `hours` ao due_date atual.
  snoozeActivity = async (id: string, hours = 1): Promise<ActivityEntry> => {
    const current = await this.repo.getActivityDueDate(id);
    const newDue = new Date(current);
    newDue.setHours(newDue.getHours() + hours);
    return this.repo.updateActivityDueDate(id, newDue.toISOString());
  };
}
